import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import Select, { StylesConfig } from 'react-select';
import { SelectValueLabel } from 'common/types/SelectValueLabel';

interface Option {
    value: string;
    label: string;
}

interface Props {
    readOnly: boolean;
    name: string;
    options?: SelectValueLabel[];
    value?: string;
    placeholder?: string;
    required?: boolean;
    onChange?: (e: { target: { name: string; value: string } }) => void;
}

const selectStyles: StylesConfig<Option, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        backgroundColor: 'white',
        borderColor: state.isFocused ? '#38bdf8' : '#ced4da',
        borderRadius: '0.375rem',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(56, 189, 248, 0.3)' : 'none',
        fontSize: '0.875rem',
        cursor: 'text',
        '&:hover': { borderColor: state.isFocused ? '#38bdf8' : '#cbd5e1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 0.75rem' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, color: '#0f172a' }),
    input: (base) => ({ ...base, color: '#0f172a', margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 0.5rem', color: '#94a3b8' }),
    clearIndicator: (base) => ({ ...base, padding: '0 0.25rem', color: '#94a3b8' }),
    menu: (base) => ({ ...base, fontSize: '0.875rem', borderRadius: '0.375rem', overflow: 'hidden', zIndex: 50 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#0ea5e9' : state.isFocused ? '#e0f2fe' : 'white',
        color: state.isSelected ? 'white' : '#0f172a',
        cursor: 'pointer',
    }),
};

const SearchableSelect: React.FC<Props> = ({ readOnly, name, options, value, placeholder, required, onChange }) => {
    const selectOptions = useMemo<Option[]>(
        () => (options ?? []).filter(o => o.value != null).map(o => ({ value: o.value!, label: o.label ?? '' })),
        [options],
    );

    const selectedOption = useMemo(
        () => selectOptions.find(o => o.value === value) ?? null,
        [selectOptions, value],
    );

    if (readOnly) {
        return (
            <Form.Control
                type="text"
                name={name}
                value={selectedOption?.label ?? ''}
                readOnly
                placeholder={placeholder}
            />
        );
    }

    return (
        <>
            <Select<Option, false>
                options={selectOptions}
                value={selectedOption}
                isSearchable
                isClearable={!required}
                placeholder={placeholder}
                styles={selectStyles}
                onChange={(opt) => {
                    onChange?.({ target: { name, value: opt?.value ?? '' } });
                }}
                filterOption={(option, raw) => {
                    if (!raw) return true;
                    return option.label.toLowerCase().includes(raw.toLowerCase());
                }}
            />
            {required && (
                <input
                    tabIndex={-1}
                    autoComplete="off"
                    style={{ opacity: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
                    value={value ?? ''}
                    required
                    onChange={() => {}}
                />
            )}
        </>
    );
};

export default SearchableSelect;
